import { useEffect, useState } from "react";
import type { DownloadThumbnailResponse } from "@freightsimple/generated-dashboard-openapi-client/models/DownloadThumbnailResponse";
import type { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import { useDocumentViewerDialog } from "@/Shared/Shipment/Dialogs/ShipmentDocumentViewerDialog";
import { ShipmentThumbnail } from "@/Shared/Shipment/CustomComponents/ShipmentThumbnail";

interface DocumentThumbnailProps {
  documentId: string | undefined;
  fetchThumbnail: (documentId: string) => Promise<DownloadThumbnailResponse>;
  type?: "pdf" | "img" | undefined;
  documentType?: DocumentType | undefined;
  documentDescription?: string | undefined;
  onDownloadDocument?: (() => Promise<void>) | undefined;
  showThumbnail: boolean;
}

const memoryCache: Record<string, string> = {};

export function ShipmentDocumentThumbnail(props: DocumentThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const documentViewerDialog = useDocumentViewerDialog();

  const { type = "pdf" } = props;

  useEffect(() => {
    // oxlint-disable-next-line sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
    const fetchThumbnailData = async () => {
      if (!props.documentId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const cacheKey = `thumbnail_${props.documentId}`;

      try {
        // Check memory cache
        let cachedThumbnail = memoryCache[cacheKey];

        // Check localStorage if not in memory
        if (!cachedThumbnail) {
          const localStorageItem = localStorage.getItem(cacheKey);
          if (localStorageItem) {
            cachedThumbnail = localStorageItem;
            memoryCache[cacheKey] = localStorageItem;
          }
        }

        if (cachedThumbnail) {
          setThumbnailUrl(cachedThumbnail);
          setIsLoading(false);
          return;
        }

        // Fetch from API
        const response = await props.fetchThumbnail(props.documentId);

        const thumbnailData = response.noImage
          ? "no-image"
          : `data:image/png;base64,${response.thumbnail}`;

        // Cache in memory and localStorage
        memoryCache[cacheKey] = thumbnailData;
        try {
          localStorage.setItem(cacheKey, thumbnailData);
        } catch {
          console.warn("localStorage quota exceeded, clearing old thumbnails");
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key?.startsWith("thumbnail_")) {
              localStorage.removeItem(key);
              if (localStorage.length < 10) break;
            }
          }
          try {
            localStorage.setItem(cacheKey, thumbnailData);
          } catch {
            console.warn(
              "Could not store in localStorage, using memory cache only",
            );
          }
        }

        setThumbnailUrl(thumbnailData);
      } catch (err) {
        setError("Error loading thumbnail");
        console.error("Error fetching thumbnail:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThumbnailData();
  }, [props.documentId, props.fetchThumbnail]);

  const handleThumbnailClick = async () => {
    if (props.documentId && props.documentType && props.documentDescription) {
      documentViewerDialog.show({
        documentId: props.documentId,
        documentDescription: props.documentDescription,
        onDownloadDocument: props.onDownloadDocument ?? (async () => {}),
      });
    }
  };

  return (
    <>
      {documentViewerDialog.dialogs}
      <ShipmentThumbnail
        handleThumbnailClick={handleThumbnailClick}
        loading={isLoading}
        error={error}
        thumbnailUrl={thumbnailUrl}
        type={type}
        showThumbnail={props.showThumbnail}
      />
    </>
  );
}
